import * as PopoverPrimitive from "@radix-ui/react-popover";
import { MapPin } from "lucide-react";
import * as React from "react";
import { Label, type LabelSize } from "../atoms/label.js";
import { cx } from "../internal/cx.js";

export type AddressAutocompleteSize = "sm" | "md" | "lg" | "xl";

/** A single autocomplete prediction returned by the data provider. */
export interface AddressSuggestion {
  /** Stable id for the suggestion (use placeId if available, else hash the text). */
  id: string;
  /** Main line of the suggestion ("123 Main Street"). */
  primaryText: string;
  /** Secondary line ("San Francisco, CA, USA"). */
  secondaryText?: string;
  /** Provider-specific id (e.g. Google placeId) — passed back on selection. */
  placeId?: string;
}

/** A resolved address (fetched after a suggestion is picked). */
export interface AddressValue {
  /** The single-line address string ("123 Main Street, San Francisco, CA 94105, USA"). */
  formatted: string;
  /** Street number + street name ("123 Main Street"). */
  line1?: string;
  /** Apartment / suite / floor ("Apt 4B"). */
  line2?: string;
  city?: string;
  /** State / province / administrative area. Short form ("CA"). */
  state?: string;
  /** Postal / ZIP code. */
  postalCode?: string;
  /** Country full name ("United States"). */
  country?: string;
  /** ISO country code, uppercased ("US"). */
  countryCode?: string;
  latitude?: number;
  longitude?: number;
  /** Provider-specific id (e.g. Google placeId). */
  placeId?: string;
}

export interface AddressAutocompleteProps {
  className?: string;
  defaultOpen?: boolean;
  /** The current input text. Controlled. */
  query?: string;
  /** Default input text when uncontrolled. */
  defaultQuery?: string;
  disabled?: boolean;
  /** Empty-state message when there are no suggestions for the current query. */
  emptyText?: string;
  error?: string;
  helperText?: string;
  id?: string;
  label?: React.ReactNode;
  /** Called when the user types in the input. */
  onQueryChange?: (query: string) => void;
  /** Called when the user picks a suggestion from the list. */
  onSuggestionSelect?: (suggestion: AddressSuggestion) => void;
  /** Optional resolved value (for single-line display, when controlled outside the picker). */
  value?: AddressValue;
  /** Called when the resolved address changes (after fetching place details). */
  onValueChange?: (value: AddressValue | undefined) => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  optional?: boolean;
  placeholder?: string;
  /** True while the provider is fetching suggestions or details. */
  isLoading?: boolean;
  /** Reason the provider is not ready (e.g. SDK still loading). Optional copy in the popover. */
  loadingText?: string;
  size?: AddressAutocompleteSize;
  /**
   * When true (default), a typed address that was not picked from the
   * suggestion list is accepted on blur as a free-form `AddressValue`.
   */
  allowManualEntry?: boolean;
  /** The list of suggestions currently to display. */
  suggestions?: AddressSuggestion[];
  /**
   * When true (default), renders a secondary input for apt/suite/floor below
   * the main field once the user has selected an address. The input edits
   * `value.line2` and emits via `onValueChange`. Set to false to suppress.
   */
  showLine2?: boolean;
  /** Label for the apt/suite sub-field. Default 'Apt, suite, floor'. */
  line2Label?: React.ReactNode;
  /** Placeholder for the apt/suite sub-field. Default 'Apt, suite, unit, floor'. */
  line2Placeholder?: string;
}

function getLabelSize(size: AddressAutocompleteSize): LabelSize {
  return size === "sm" ? "sm" : size === "xl" ? "lg" : "md";
}

/** Accept a typed single-line address when autocomplete is unavailable or skipped. */
export function createManualAddressValue(query: string): AddressValue | undefined {
  const formatted = query.trim().replace(/\s{2,}/g, " ");
  if (formatted.length < 5 || !/[A-Za-z]/.test(formatted)) return undefined;
  return { formatted, line1: formatted };
}

export function isManualAddressQueryValid(query: string): boolean {
  return createManualAddressValue(query) !== undefined;
}

function useControllableState<T>(
  controlled: T | undefined,
  defaultValue: T | undefined,
  onChange?: (value: T) => void,
) {
  const isControlled = controlled !== undefined;
  const [internal, setInternal] = React.useState<T | undefined>(defaultValue);
  const value = isControlled ? controlled : internal;
  const setValue = React.useCallback(
    (next: T) => {
      if (!isControlled) setInternal(next);
      onChange?.(next);
    },
    [isControlled, onChange],
  );
  return [value, setValue] as const;
}

/**
 * AddressAutocomplete — typeable address field with a suggestion popover and
 * a follow-up apt/unit input.
 *
 * The component is presentational — it renders the field, the dropdown, and
 * the secondary line2 input, but the consumer is responsible for fetching
 * suggestions and resolving the selected address (typically via Google
 * Places, Mapbox, or a custom backend).
 */
export function AddressAutocomplete({
  className,
  defaultOpen,
  defaultQuery,
  disabled,
  emptyText = "No matches found",
  error,
  helperText,
  id,
  isLoading,
  label,
  loadingText = "Loading suggestions…",
  onOpenChange,
  onQueryChange,
  onSuggestionSelect,
  onValueChange,
  open,
  optional = false,
  placeholder = "Start typing your address",
  query,
  size = "md",
  suggestions,
  value,
  showLine2 = true,
  line2Label,
  line2Placeholder,
  allowManualEntry = true,
}: AddressAutocompleteProps) {
  const generatedId = React.useId();
  const inputId = id ?? generatedId;
  const listboxId = `${inputId}-listbox`;
  const helperId = helperText && !error ? `${inputId}-helper` : undefined;
  const errorId = error ? `${inputId}-error` : undefined;
  const describedBy = errorId ?? helperId;

  // The visible input text. When the user picks a suggestion, we sync this to
  // the resolved formatted address so the field reflects the chosen place.
  const [queryValue, setQueryValue] = useControllableState<string>(
    query,
    defaultQuery ?? value?.formatted ?? "",
    onQueryChange,
  );

  const [internalOpen, setInternalOpen] = React.useState<boolean>(Boolean(defaultOpen));
  const isOpenControlled = open !== undefined;
  const isOpen = isOpenControlled ? open : internalOpen;
  const setOpen = (next: boolean) => {
    if (!isOpenControlled) setInternalOpen(next);
    onOpenChange?.(next);
  };

  // Index of the currently keyboard-highlighted suggestion in the list.
  const [activeIndex, setActiveIndex] = React.useState<number>(-1);
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  // Reset highlight whenever the suggestion list changes — the prior index
  // might point past the new list length.
  React.useEffect(() => {
    setActiveIndex((current) => {
      if (!suggestions || suggestions.length === 0) return -1;
      if (current < 0 || current >= suggestions.length) return 0;
      return current;
    });
  }, [suggestions]);

  // When the resolved address changes from outside, update the visible field
  // unless the user has it focused (avoid clobbering an in-progress edit).
  // biome-ignore lint/correctness/useExhaustiveDependencies: setQueryValue is stable from useControllableState; reacting to value changes only is intentional.
  React.useEffect(() => {
    if (!value) return;
    if (document.activeElement === inputRef.current) return;
    if (queryValue === value.formatted) return;
    setQueryValue(value.formatted);
  }, [value]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = event.target.value;
    setQueryValue(next);
    if (next.trim() === "") {
      setOpen(false);
      onValueChange?.(undefined);
    } else {
      setOpen(true);
    }
  };

  const handleSelect = (suggestion: AddressSuggestion) => {
    onSuggestionSelect?.(suggestion);
    setOpen(false);
  };

  const hasSuggestions = !!suggestions && suggestions.length > 0;

  const handleBlur = () => {
    if (!allowManualEntry) {
      setOpen(false);
      return;
    }

    const trimmed = (queryValue ?? "").trim();
    if (!trimmed) {
      onValueChange?.(undefined);
      setOpen(false);
      return;
    }

    if (value?.formatted?.trim() === trimmed) {
      setOpen(false);
      return;
    }

    const manualValue = createManualAddressValue(trimmed);
    if (manualValue) {
      onValueChange?.(manualValue);
      setQueryValue(manualValue.formatted);
    }

    setOpen(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (!isOpen) {
        setOpen(true);
        return;
      }
      if (hasSuggestions && suggestions) {
        setActiveIndex((index) => (index + 1) % suggestions.length);
      }
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      if (!isOpen) {
        setOpen(true);
        return;
      }
      if (hasSuggestions && suggestions) {
        setActiveIndex((index) => (index <= 0 ? suggestions.length - 1 : index - 1));
      }
      return;
    }
    if (event.key === "Enter" && isOpen && activeIndex >= 0 && hasSuggestions && suggestions) {
      const next = suggestions[activeIndex];
      if (next) {
        event.preventDefault();
        handleSelect(next);
      }
      return;
    }
    if (event.key === "Escape" && isOpen) {
      event.preventDefault();
      setOpen(false);
    }
  };

  const filled = (queryValue ?? "").length > 0;
  const activeOptionId =
    activeIndex >= 0 && hasSuggestions && suggestions
      ? `${listboxId}-${suggestions[activeIndex]?.id}`
      : undefined;

  // The apt/unit input only makes sense once a real address has been resolved.
  // Until then, there is no AddressValue to edit.
  const showAptInput = showLine2 && value !== undefined;
  const line2InputId = `${inputId}-line2`;
  const handleLine2Change = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!value) return;
    const next = event.target.value;
    onValueChange?.({ ...value, line2: next.trim() === "" ? undefined : next });
  };

  return (
    <div className="cw-v2-input-field">
      {label ? (
        <Label htmlFor={inputId} optional={optional} size={getLabelSize(size)}>
          {label}
        </Label>
      ) : null}

      <PopoverPrimitive.Root open={isOpen} onOpenChange={setOpen}>
        <PopoverPrimitive.Anchor asChild>
          <div
            className={cx(
              "cw-v2-input",
              `cw-v2-input--${size}`,
              "cw-v2-address-autocomplete__field",
              error && "cw-v2-input--error",
              disabled && "cw-v2-input--disabled",
              filled && "cw-v2-input--filled",
              className,
            )}
          >
            <span className="cw-v2-input__affix" aria-hidden>
              <MapPin className="cw-v2-address-autocomplete__icon" />
            </span>
            <input
              ref={inputRef}
              id={inputId}
              type="text"
              role="combobox"
              aria-autocomplete="list"
              aria-controls={listboxId}
              aria-expanded={isOpen}
              aria-activedescendant={activeOptionId}
              className="cw-v2-input__control cw-v2-address-autocomplete__input"
              value={queryValue ?? ""}
              onChange={handleChange}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
              onFocus={() => {
                if (queryValue && queryValue.trim() !== "") setOpen(true);
              }}
              placeholder={placeholder}
              disabled={disabled}
              aria-describedby={describedBy}
              aria-invalid={error ? true : undefined}
              autoCapitalize="words"
              autoComplete="off"
              autoCorrect="off"
              enterKeyHint="search"
              inputMode="search"
              spellCheck={false}
            />
          </div>
        </PopoverPrimitive.Anchor>

        <PopoverPrimitive.Portal>
          <PopoverPrimitive.Content
            align="start"
            sideOffset={6}
            className="cw-v2-theme cw-v2-popover__content cw-v2-address-autocomplete__content"
            // Keep focus in the input — the user types and arrow-keys to pick.
            onOpenAutoFocus={(event) => event.preventDefault()}
          >
            <ul
              id={listboxId}
              // biome-ignore lint/a11y/noNoninteractiveElementToInteractiveRole: the WAI-ARIA combobox pattern is standardly authored as <ul role=listbox>; <select> cannot host the sibling search input.
              // biome-ignore lint/a11y/useSemanticElements: the WAI-ARIA combobox pattern requires role=listbox on the dropdown sibling.
              role="listbox"
              // tabIndex=-1: keep the listbox reachable programmatically without putting it in the tab order — the combobox keeps focus on the input and tracks selection via aria-activedescendant.
              tabIndex={-1}
              className="cw-v2-address-autocomplete__list"
              aria-label="Address suggestions"
            >
              {isLoading ? (
                <li className="cw-v2-address-autocomplete__state" role="presentation">
                  {loadingText}
                </li>
              ) : !hasSuggestions || !suggestions ? (
                <li className="cw-v2-address-autocomplete__state" role="presentation">
                  {emptyText}
                </li>
              ) : (
                suggestions.map((suggestion, index) => {
                  const isActive = index === activeIndex;
                  return (
                    <li
                      key={suggestion.id}
                      id={`${listboxId}-${suggestion.id}`}
                      // biome-ignore lint/a11y/noNoninteractiveElementToInteractiveRole: <li role=option> is the standard listbox option markup in the WAI-ARIA combobox pattern.
                      // biome-ignore lint/a11y/useSemanticElements: the WAI-ARIA combobox pattern requires role=option on each <li>.
                      role="option"
                      // tabIndex=-1: options are tracked via aria-activedescendant on the input — they're focusable programmatically (e.g. for screen-reader virtual cursor) but never in the tab order.
                      tabIndex={-1}
                      aria-selected={isActive}
                      data-active={isActive ? "true" : undefined}
                      className={cx(
                        "cw-v2-address-autocomplete__option",
                        isActive && "cw-v2-address-autocomplete__option--active",
                      )}
                      onMouseDown={(event) => {
                        event.preventDefault();
                        handleSelect(suggestion);
                      }}
                      onMouseEnter={() => setActiveIndex(index)}
                    >
                      <MapPin className="cw-v2-address-autocomplete__option-icon" aria-hidden />
                      <div className="cw-v2-address-autocomplete__option-text">
                        <span className="cw-v2-address-autocomplete__option-primary">
                          {suggestion.primaryText}
                        </span>
                        {suggestion.secondaryText ? (
                          <span className="cw-v2-address-autocomplete__option-secondary">
                            {suggestion.secondaryText}
                          </span>
                        ) : null}
                      </div>
                    </li>
                  );
                })
              )}
            </ul>
          </PopoverPrimitive.Content>
        </PopoverPrimitive.Portal>
      </PopoverPrimitive.Root>

      {showAptInput ? (
        <div className="cw-v2-address-autocomplete__line2">
          <Label htmlFor={line2InputId} optional size={getLabelSize(size)}>
            {line2Label ?? "Apt, suite, floor"}
          </Label>
          <div
            className={cx(
              "cw-v2-input",
              `cw-v2-input--${size}`,
              error && "cw-v2-input--error",
              disabled && "cw-v2-input--disabled",
            )}
          >
            <input
              id={line2InputId}
              type="text"
              className="cw-v2-input__control"
              value={value?.line2 ?? ""}
              onChange={handleLine2Change}
              placeholder={line2Placeholder ?? "Apt, suite, unit, floor"}
              disabled={disabled}
              autoCapitalize="characters"
              autoComplete="address-line2"
              autoCorrect="off"
              spellCheck={false}
              inputMode="text"
            />
          </div>
        </div>
      ) : null}

      {error ? (
        <p className="cw-v2-input__message cw-v2-input__message--error" id={errorId}>
          {error}
        </p>
      ) : helperText ? (
        <p className="cw-v2-input__message" id={helperId}>
          {helperText}
        </p>
      ) : null}
    </div>
  );
}

AddressAutocomplete.displayName = "AddressAutocomplete";
